/**
 * Tier 2 — silence detection.
 *
 * Decodes short windows of the audio via WebAudio and measures RMS energy to
 * tell a muted/silent track from an audible one. Unlike the container tier this
 * DOES decode audio, so it only works for codecs the browser can decode — hence
 * it is fail-open: any failure (unsupported codec, blocked autoplay, timeout)
 * returns `undetermined` and the caller lets the file through.
 */

export type AudioLoudness = 'audible' | 'silent' | 'undetermined';

/** Interior sample points — skip the first/last 15% so a quiet intro/outro
 * isn't mistaken for a muted track. */
const SAMPLE_POSITIONS = [0.15, 0.5, 0.85];
const WINDOW_MS = 350;
/** ~ -50 dBFS. Below this across every sample ⇒ treat as silent/muted. */
const RMS_SILENCE_THRESHOLD = 0.003;
const OVERALL_TIMEOUT_MS = 15_000;

const rms = (data: Float32Array): number => {
  let sum = 0;
  for (let i = 0; i < data.length; i += 1) sum += data[i] * data[i];
  return Math.sqrt(sum / data.length);
};

const onceEvent = (target: HTMLMediaElement, event: string): Promise<void> =>
  new Promise(resolve => {
    const handler = (): void => {
      target.removeEventListener(event, handler);
      resolve();
    };
    target.addEventListener(event, handler);
  });

const nextFrame = (): Promise<void> =>
  new Promise(resolve => {
    requestAnimationFrame(() => resolve());
  });

/* eslint-disable no-await-in-loop -- windows are sampled sequentially by design */
async function measureLoudness(file: File): Promise<AudioLoudness> {
  if (typeof window === 'undefined' || !window.AudioContext) {
    return 'undetermined';
  }

  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.preload = 'auto';
  video.src = url;
  const ctx = new AudioContext();

  try {
    await onceEvent(video, 'loadedmetadata');
    const { duration } = video;
    if (!Number.isFinite(duration) || duration <= 0) return 'undetermined';

    const source = ctx.createMediaElementSource(video);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    // Intentionally NOT connected to ctx.destination → inaudible to the user.

    const buffer = new Float32Array(analyser.fftSize);
    let maxRms = 0;

    for (const position of SAMPLE_POSITIONS) {
      video.currentTime = Math.min(
        duration * position,
        Math.max(0, duration - 0.5),
      );
      await onceEvent(video, 'seeked');
      await ctx.resume();
      await video.play();

      const deadline = Date.now() + WINDOW_MS;
      while (Date.now() < deadline) {
        analyser.getFloatTimeDomainData(buffer);
        const level = rms(buffer);
        if (level > maxRms) maxRms = level;
        await nextFrame();
      }
      video.pause();

      // Early out as soon as any window is clearly audible.
      if (maxRms >= RMS_SILENCE_THRESHOLD) return 'audible';
    }

    return maxRms >= RMS_SILENCE_THRESHOLD ? 'audible' : 'silent';
  } finally {
    video.pause();
    video.removeAttribute('src');
    URL.revokeObjectURL(url);
    void ctx.close();
  }
}
/* eslint-enable no-await-in-loop */

/**
 * Detect whether a video's audio is (near) silent. Fail-open: returns
 * `undetermined` whenever the browser can't decode/analyse the audio, or the
 * check exceeds its time budget.
 */
export async function detectLoudness(file: File): Promise<AudioLoudness> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<AudioLoudness>(resolve => {
    timer = setTimeout(() => resolve('undetermined'), OVERALL_TIMEOUT_MS);
  });
  try {
    return await Promise.race([measureLoudness(file), timeout]);
  } catch {
    return 'undetermined';
  } finally {
    if (timer) clearTimeout(timer);
  }
}
